import PropTypes from 'prop-types';
import { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

// project imports
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';

import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, handlerDrawerPeek, isDrawerVisualOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const visualOpen = isDrawerVisualOpen(menuMaster);
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const peeking = Boolean(menuMaster.isDrawerPeeking && !drawerOpen && !downLG);

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={visualOpen} />, [visualOpen]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, zIndex: 1200, width: { lg: drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH } }}
      aria-label="mailbox folders"
      onMouseEnter={() => {
        if (!downLG && !drawerOpen) handlerDrawerPeek(true);
      }}
      onMouseLeave={() => {
        if (!downLG && !drawerOpen) handlerDrawerPeek(false);
      }}
    >
      {!downLG ? (
        <MiniDrawerStyled
          variant="permanent"
          open={visualOpen}
          sx={
            peeking
              ? {
                  width: MINI_DRAWER_WIDTH,
                  '& .MuiDrawer-paper': {
                    position: 'absolute',
                    zIndex: 1300
                  }
                }
              : undefined
          }
        >
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' } }}
          slotProps={{
            paper: {
              sx: {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                borderRight: '1px solid',
                borderRightColor: 'divider',
                boxShadow: 'inherit'
              }
            }
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
